import React, { forwardRef } from "react";
import { ProductSource } from "../../api/decimal/methods/product.ts";

// Derived from backend
type CoverWidth =
  | 20
  | 25
  | 28
  | 30
  | 32
  | 35
  | 60
  | 120
  | 150
  | 200
  | 240
  | 320
  | 480
  | 640
  | 720
  | 768
  | 960
  | 1024;

export type CoverStyle = "default" | "preview";

export type ImageSource = ProductSource | null | undefined;

export const unknownPrintSource = "Akademos";

interface AssetCoverProps extends React.ComponentPropsWithRef<"img"> {
  vbid: string;
  width: CoverWidth;
  width2x?: CoverWidth;
  /**
   * Fallback image style
   *   preview = gray
   *   default = orange
   */
  imageStyle?: CoverStyle;
  /**
   * What subsystem to source the image from. Null/undefined is the base
   * covers service. Matches product source (currently only Akademos) for print.
   */
  source?: ImageSource;
}

/**
 * Simple width-based cover image. Height varies by content and some covers may be landscape.
 */
const AssetCover = forwardRef<HTMLImageElement, AssetCoverProps>(
  function AssetCover(
    { vbid, width, width2x, alt = "", imageStyle = "preview", source, ...rest },
    ref
  ) {
    function getUrl(imageWidth: CoverWidth) {
      const params = new URLSearchParams();
      if (imageStyle !== "default") {
        params.set("style", imageStyle);
      }
      if (source) {
        params.set("source", source);
      }

      const queryString = params.size > 0 ? `?${params.toString()}` : "";

      return `https://${
        import.meta.env.VITE_COVERS_HOST
      }/vbid/${vbid}/width/${imageWidth}${queryString}`;
    }

    return (
      <img
        {...rest}
        src={getUrl(width)}
        srcSet={width2x ? `${getUrl(width2x)} 2x` : undefined}
        alt={alt}
        ref={ref}
      />
    );
  }
);

export default AssetCover;
